import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";

// Template135: Día 3 - DDP e Ihwa Mural Village (con mapa)
const Template135 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const { setMapFullscreen } = useMapFullscreen() || {};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator === "undefined") return;
		setIsOnline(navigator.onLine);
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page?.texts?.[0]?.content;
	const mapUrl = page?.maps?.[0];
	const tipIhwa = page?.texts?.[1]?.content;
	const ihwaTitle = page?.texts?.[2]?.content;
	const ihwaDesc = page?.texts?.[3]?.content;
	const sabiasQueMural = page?.texts?.[4]?.content;
	const ddpTitle = page?.texts?.[5]?.content;
	const ddpDesc = page?.texts?.[6]?.content;
	const sabiasQueDdp = page?.texts?.[7]?.content;
	const comoLlegarTitle = page?.texts?.[8]?.content;
	const comoLlegarText = page?.texts?.[9]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const ddpImage = getImageSrc("ddp");
	const muralImage = getImageSrc("mural");
	const muralSticker = getCanvasImageUrl("mural-sticker");
	const ddpSticker = getCanvasImageUrl("ddp-sticker");
	const logoImage = getCanvasImageUrl("logo");

	const actualHeaderColor = headerBackgroundColor || "#2E8B80";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: "#444",
		margin: 0,
	};

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#2E8B80",
					}}
				>
					<button
						onClick={() => setFullscreen(false)}
						style={{
							backgroundColor: "#fff",
							color: "#2E8B80",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</button>
				</Box>
				<iframe
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{mapUrl && (
				<Box
					data-block-id="map"
					sx={{
						display: "flex",
						flexDirection: "column",
						backgroundColor: "#f7f7f7",
						position: "relative",
						marginTop: "-60px",
						minHeight: 300,
					}}
				>
					<Box sx={{ flex: 1, marginTop: "-70px" }}>
						{isOnline && (
							<button
								onClick={() => setFullscreen(true)}
								style={{
									position: "absolute",
									top: 70,
									right: 10,
									backgroundColor: "#2E8B80",
									color: "#fff",
									padding: "8px 10px",
									fontSize: "11px",
									borderRadius: "8px",
									fontFamily: fontFamily || "'Poppins', sans-serif",
									boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

									border: "none",
									cursor: "pointer",
								}}
							>
								Ver mapa grande
							</button>
						)}
						<iframe
							title="Mapa DDP"
							src={mapUrl}
							width="100%"
							height="400px"
							allowFullScreen
							loading="lazy"
							style={{ border: 0 }}
						/>
					</Box>
					{muralSticker && (
						<img
							src={muralSticker}
							alt=""
							style={{
								width: 100,
								position: "absolute",
								bottom: -20,
								right: -10,
							}}
						/>
					)}
					{tipIhwa && (
						<Box
							sx={{
								position: "absolute",
								bottom: 60,
								right: 30,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 130,
							}}
						>
							{tipIhwa}
						</Box>
					)}
				</Box>
			)}

			<Box
				sx={{
					padding: "10px 12px",
					position: "relative",
					marginTop: isSmallScreen ? "20px" : 0,
				}}
			>
				{(ihwaTitle || ihwaDesc || muralImage) && (
					<Box data-block-id="text-1" sx={{ display: "flex", gap: "16px", marginTop: "16px" }}>
						{muralImage && (
							<img
								src={muralImage}
								alt=""
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<Box sx={{ width: "60%", position: "relative" }}>
							{ihwaTitle && (
								<Box component="h3" sx={h3}>
									{ihwaTitle}
								</Box>
							)}
							{ihwaDesc && (
								<Box component="p" sx={p}>
									{ihwaDesc}
								</Box>
							)}
							{sabiasQueMural && (
								<SabiasQue icono="🎨" texto={sabiasQueMural} />
							)}
						</Box>
					</Box>
				)}

				{(ddpTitle || ddpDesc || ddpImage) && (
					<Box
						data-block-id="text-2"
						sx={{
							display: "flex",
							gap: "16px",
							position: "relative",
							marginTop: "16px",
						}}
					>
						{ddpImage && (
							<img
								src={ddpImage}
								alt=""
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							{ddpTitle && (
								<Box component="h3" sx={h3}>
									{ddpTitle}
								</Box>
							)}
							{ddpDesc && (
								<Box component="p" sx={p}>
									{ddpDesc}
								</Box>
							)}
							{sabiasQueDdp && (
								<Box sx={{ position: "absolute", bottom: -5, left: "32%" }}>
									<SabiasQue icono="🌃" texto={sabiasQueDdp} />
								</Box>
							)}
						</Box>
					</Box>
				)}

				{(comoLlegarTitle || comoLlegarText) && (
					<Box
						data-block-id="text-3"
						sx={{
							backgroundColor: "#caeae6",
							border: "3px solid #2e8b80",
							borderRadius: "12px",
							padding: "12px 12px",
							fontSize: 12,
							fontFamily: "'Poppins', sans-serif",
							color: "#333",
							position: "relative",
							marginTop: "16px",
						}}
					>
						<Box sx={{ width: isSmallScreen ? "70%" : "100%" }}>
							{comoLlegarTitle && (
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: 13,
										fontWeight: 700,
										color: "#2e8b80",
										textTransform: "uppercase",
									}}
								>
									{comoLlegarTitle}
								</Box>
							)}
							{comoLlegarText && (
								<Box
									component="p"
									sx={{ ...p, width: "64%" }}
									dangerouslySetInnerHTML={{ __html: comoLlegarText }}
								/>
							)}
						</Box>

						{ddpSticker && (
							<img
								src={ddpSticker}
								alt=""
								style={{
									width: 120,
									position: "absolute",
									bottom: 0,
									right: isSmallScreen ? 0 : 0,
								}}
							/>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template135.thumbnail = "/images/template-1.png";

export default Template135;
